import { RefObject, useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';

const DEFAULT_SCROLL_STATE = { canScrollLeft: false, canScrollRight: false };

export function useMenuCollectionsScroll(containerRef: RefObject<HTMLDivElement>) {
  const [scrollState, setScrollState] = useState(DEFAULT_SCROLL_STATE);

  const handleScrollX = useCallback(
    ({ direction, distance }: { direction: 'right' | 'left'; distance: number }) => {
      if (!containerRef.current) {
        return;
      }

      const distanceForDirection = direction === 'left' ? distance * -1 : distance;

      containerRef.current.scrollBy({ left: distanceForDirection, behavior: 'smooth' });
    },
    [containerRef],
  );

  const computeScrollState = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const containerWidth = containerRef.current.getBoundingClientRect().width;

    if (containerRef.current.scrollWidth === containerWidth) {
      return DEFAULT_SCROLL_STATE;
    }

    const canScrollLeft = containerRef.current.scrollLeft > 0;
    const canScrollRight =
      containerRef.current.scrollLeft + containerRef.current.clientWidth <
      containerRef.current.scrollWidth;

    setScrollState({
      canScrollLeft,
      canScrollRight,
    });
  }, [containerRef]);

  useEffect(() => {
    const currentContainerRef = containerRef.current;

    if (currentContainerRef) {
      computeScrollState();

      currentContainerRef.addEventListener('scroll', debounce(computeScrollState, 200));
      // We know window exists, as this is added in a useEffect
      window.addEventListener('resize', debounce(computeScrollState, 200));
    }

    return () => {
      if (currentContainerRef) {
        currentContainerRef.removeEventListener('scroll', computeScrollState);
      }

      window.removeEventListener('resize', computeScrollState);
    };
  }, [containerRef, computeScrollState]);

  return { ...scrollState, handleScrollX };
}
